import { useState, useEffect } from 'react';
import { MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export const DonenVitrin = ({ items, onItemClick, getCategoryName }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  
  const itemCount = items.length;

  // Auto-rotate - stops on hover
  useEffect(() => {
    if (itemCount === 0 || isHovering) return;
    
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % itemCount);
    }, 3500);

    return () => clearInterval(interval);
  }, [itemCount, isHovering]);

  const handlePrev = () => {
    setActiveIndex(prev => prev <= 0 ? itemCount - 1 : prev - 1);
  };

  const handleNext = () => {
    setActiveIndex(prev => (prev + 1) % itemCount);
  };

  if (itemCount === 0) return null;

  // Get visible items (active + 2 on each side)
  const getVisibleItems = () => {
    const visible = [];
    for (let i = -2; i <= 2; i++) {
      let idx = activeIndex + i;
      // Wrap around
      if (idx < 0) idx = itemCount + idx;
      if (idx >= itemCount) idx = idx % itemCount;
      visible.push({ item: items[idx], offset: i, originalIndex: idx });
    }
    return visible;
  };

  const visibleItems = getVisibleItems();

  return (
    <div 
      className="relative py-6" 
      data-testid="donen-vitrin"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Navigation Buttons */}
      <Button 
        variant="secondary" 
        size="icon" 
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 rounded-full bg-white hover:bg-gray-100 text-black shadow-xl border-0"
        onClick={handlePrev}
        data-testid="donen-vitrin-prev"
      >
        <ChevronLeft className="w-5 h-5" />
      </Button>
      
      <Button 
        variant="secondary" 
        size="icon" 
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 rounded-full bg-white hover:bg-gray-100 text-black shadow-xl border-0"
        onClick={handleNext}
        data-testid="donen-vitrin-next"
      >
        <ChevronRight className="w-5 h-5" />
      </Button>

      {/* Carousel - 5 visible cards centered */}
      <div className="flex items-center justify-center gap-4 px-16 h-72">
        {visibleItems.map(({ item, offset, originalIndex }) => {
          const isCenter = offset === 0;
          const isAdjacent = Math.abs(offset) === 1;
          
          // Size and opacity based on position
          let scale = isCenter ? 1 : isAdjacent ? 0.85 : 0.7;
          let opacity = isCenter ? 1 : isAdjacent ? 0.7 : 0.4;
          let zIndex = isCenter ? 20 : isAdjacent ? 10 : 5;
          let width = isCenter ? 320 : isAdjacent ? 280 : 240;
          
          return (
            <div
              key={`${item.id}-${offset}`}
              className="transition-all duration-500 ease-out cursor-pointer flex-shrink-0"
              style={{ 
                transform: `scale(${scale})`,
                opacity,
                zIndex,
                width: `${width}px`
              }}
              onClick={() => {
                if (isCenter) {
                  onItemClick(item);
                } else {
                  setActiveIndex(originalIndex);
                }
              }}
              data-testid={`donen-vitrin-item-${item.id}`}
            >
              <div
                className={`relative h-64 rounded-xl overflow-hidden transition-shadow duration-300 ${
                  isCenter 
                    ? 'ring-4 ring-amber-400 shadow-2xl shadow-amber-500/40' 
                    : 'shadow-lg'
                }`}
              >
                {/* Background Image */}
                {item.cover_image_url ? (
                  <img 
                    src={item.cover_image_url} 
                    alt={item.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-600" />
                )}
                
                {/* Light gradient overlay - only at bottom */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <Badge className="mb-2 bg-amber-500 text-black border-0 font-semibold text-xs">
                    {getCategoryName(item.category_id)}
                  </Badge>
                  <h3 className="font-heading text-lg font-bold text-white drop-shadow-lg line-clamp-2">
                    {item.name}
                  </h3>
                  {item.district && (
                    <p className="text-white/90 text-sm flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> {item.district}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-4">
        {items.map((_, index) => (
          <button
            key={index}
            className={`rounded-full transition-all duration-300 ${
              index === activeIndex 
                ? 'w-8 h-2 bg-amber-500' 
                : 'w-2 h-2 bg-white/40 hover:bg-white/60'
            }`}
            onClick={() => setActiveIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default DonenVitrin;
